import * as React from 'react';

import { type UnappliedFiltersByFacet } from 'bundles/search-common/components/experiments/filters2024/CheckableExpandableSearchFilterGroup';
import { type FilterChipDropdownProps } from 'bundles/search-common/components/experiments/filters2024/FilterChipDropdown';
import { IS_PART_OF_COURSERA_PLUS } from 'bundles/search-common/constants/filter';
import { deconstructFilter } from 'bundles/search-common/utils/utils';

import _t from 'i18n!nls/search-common';

export const FILTER_DROPDOWN_KEYS = [
  'topic',
  'productDuration',
  'productTypeDescription',
  'language',
  'productDifficultyLevel',
];

export const BINARY_FILTER_KEYS = ['isPartOfCourseraPlus'];

export const getFilterDropdownPropsMap = (): Record<string, Partial<FilterChipDropdownProps>> => ({
  topic: {
    triggerLabel: _t('Topic'),
  },
  productTypeDescription: {
    triggerLabel: _t('Learning Product'),
  },
  productDifficultyLevel: {
    triggerLabel: _t('Level'),
  },
  language: {
    triggerLabel: _t('Language'),
  },
  productDuration: {
    triggerLabel: _t('Duration'),
  },
});

export function getCountsPerKey(userFilters?: string[][]) {
  const counts: Record<string, number> = {};

  userFilters?.forEach((subArray) => {
    subArray.forEach((item) => {
      const { facetName } = deconstructFilter(item);
      if (counts[facetName]) {
        counts[facetName] += 1;
      } else {
        counts[facetName] = 1;
      }
    });
  });

  return counts;
}

export function getSingleFilterValueForKey(
  filterKey: string | undefined,
  userFilters: string[][] | undefined
): string | undefined {
  if (!filterKey || !userFilters) {
    return undefined;
  }

  for (const subArray of userFilters) {
    for (const item of subArray) {
      const { facetName, facetValue } = deconstructFilter(item);
      if (facetName === filterKey) {
        return facetValue;
      }
    }
  }

  return undefined;
}

type QuickFilterProps = {
  label: string;
  prefix?: React.ReactNode;
  values: string[];
};

export const getQuickFilterProps = (): QuickFilterProps[] => {
  return [
    {
      label: _t('Courses'),
      prefix: (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M4.01574 1.5C3.87248 1.51095 3.73873 1.57658 3.64236 1.68354C3.54339 1.79341 3.49188 1.93795 3.49909 2.08565L3.49968 2.09782V12.438L3.49839 12.4559C3.48544 12.6358 3.52749 12.8155 3.61897 12.971C3.71045 13.1265 3.84704 13.2506 4.01062 13.3267L4.02143 13.3317L4.03199 13.3372C4.0895 13.3674 4.15233 13.3861 4.21699 13.3922L4.23351 13.3938L10.746 14.4522L12.4997 11.541V1.5H4.01574ZM3.99968 1V0.5H13.4997V11.819L11.2534 15.5478L4.1042 14.3859C3.92069 14.3662 3.74237 14.3128 3.57822 14.2284C3.2355 14.0663 2.94934 13.8049 2.75704 13.478C2.56604 13.1533 2.47651 12.7791 2.49968 12.4034V2.12101C2.48333 1.71422 2.62667 1.31694 2.8994 1.01421C3.17511 0.708167 3.56068 0.523596 3.97197 0.500768L3.99968 1Z"
            fill="currentColor"
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M3.49945 2.12525C3.49489 2.27467 3.5482 2.42008 3.64826 2.53114C3.74832 2.64221 3.8874 2.71035 4.03648 2.72136L4.05699 2.72287L11.4997 3.89244V15H10.4997V4.74756L3.94045 3.71683C3.54281 3.68178 3.17285 3.49746 2.9053 3.20048C2.63272 2.89792 2.48749 2.5018 2.49991 2.09475L3.49945 2.12525Z"
            fill="currentColor"
          />
        </svg>
      ),
      values: ['productTypeDescription:Courses'],
    },
    {
      label: _t('Professional Certificates'),
      prefix: (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M0.5 3.5H15.5V13.5H0.5V3.5ZM1.5 4.5V12.5H14.5V4.5H1.5Z"
            fill="currentColor"
          />
          <path fillRule="evenodd" clipRule="evenodd" d="M3.61 13V4H4.61V13H3.61Z" fill="currentColor" />
          <path fillRule="evenodd" clipRule="evenodd" d="M11.39 13V4H12.39V13H11.39Z" fill="currentColor" />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M6.9403 2.70726C6.74723 2.86622 6.5 3.21019 6.5 4H5.5C5.5 2.97981 5.83277 2.32378 6.3047 1.93524C6.75588 1.56378 7.25849 1.5 7.5 1.5H8.5C8.74151 1.5 9.24412 1.56378 9.6953 1.93524C10.1672 2.32378 10.5 2.97981 10.5 4H9.5C9.5 3.21019 9.25277 2.86622 9.0597 2.70726C8.84588 2.53122 8.59849 2.5 8.5 2.5H7.5C7.40151 2.5 7.15412 2.53122 6.9403 2.70726Z"
            fill="currentColor"
          />
        </svg>
      ),
      values: ['productTypeDescription:Professional Certificates'],
    },
    {
      label: _t('Degrees'),
      prefix: (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M7.90052 2.4375L14.7659 6.00084L7.90052 9.56417L1.03516 6.00084L7.90052 2.4375ZM3.20588 6.00084L7.90052 8.4375L12.5952 6.00084L7.90052 3.56417L3.20588 6.00084Z"
            fill="currentColor"
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M4.50052 9.67084V7.50084H5.50052V9.67084C5.50052 10.0119 5.69839 10.3746 6.12724 10.6728C6.55433 10.9699 7.17418 11.1708 7.89052 11.1708C8.60686 11.1708 9.22671 10.9699 9.65379 10.6728C10.0826 10.3746 10.2805 10.0119 10.2805 9.67084V7.50084H11.2805V9.67084C11.2805 10.4298 10.8384 11.0671 10.2247 11.4938C9.60933 11.9218 8.78418 12.1708 7.89052 12.1708C6.99686 12.1708 6.17171 11.9218 5.55629 11.4938C4.94265 11.0671 4.50052 10.4298 4.50052 9.67084Z"
            fill="currentColor"
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M7.45681 5.77035C7.58411 5.5253 7.88595 5.42984 8.131 5.55713L13.271 8.22713C13.4366 8.31315 13.5405 8.48423 13.5405 8.67084V10.3308C13.5405 10.607 13.3167 10.8308 13.0405 10.8308C12.7644 10.8308 12.5405 10.607 12.5405 10.3308V8.97454L7.67003 6.44454C7.42498 6.31725 7.32952 6.0154 7.45681 5.77035Z"
            fill="currentColor"
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M12.3709 9.72387C12.5776 9.62172 12.805 9.56859 13.0355 9.56859C13.266 9.56859 13.4935 9.62172 13.7001 9.72387C13.9068 9.82601 14.0872 9.97442 14.2272 10.1576C14.3672 10.3407 14.4631 10.5537 14.5075 10.7799C14.5471 10.9821 14.5447 11.19 14.5005 11.3907V14.5008H11.5705V11.3907C11.5264 11.19 11.5239 10.9821 11.5636 10.7799C11.6079 10.5537 11.7039 10.3407 11.8439 10.1576C11.9839 9.97442 12.1642 9.82601 12.3709 9.72387ZM13.0355 10.5686C12.9587 10.5686 12.8829 10.5863 12.814 10.6203C12.7451 10.6544 12.685 10.7039 12.6383 10.7649C12.5916 10.826 12.5597 10.8969 12.5449 10.9723C12.5301 11.0478 12.5329 11.1256 12.553 11.1997L12.5705 11.2641V13.5008H13.5005V11.2641L13.518 11.1997C13.5382 11.1256 13.541 11.0478 13.5262 10.9723C13.5114 10.8969 13.4794 10.826 13.4327 10.7649C13.3861 10.7039 13.3259 10.6544 13.2571 10.6203C13.1882 10.5863 13.1124 10.5686 13.0355 10.5686Z"
            fill="currentColor"
          />
        </svg>
      ),
      values: ['productTypeDescription:Degrees'],
    },
    {
      label: _t('Beginner'),
      values: ['productDifficultyLevel:Beginner'],
    },
    {
      label: _t('Advanced'),
      values: ['productDifficultyLevel:Advanced'],
    },
  ];
};

export const getFiltersByFacet = (facetFilters: string[][] | undefined): UnappliedFiltersByFacet => {
  if (facetFilters) {
    return facetFilters.reduce((accumulatedFacetValues, currentFacetValue) => {
      // @ts-expect-error TS2532 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
      const facet = currentFacetValue[0].split(':')[0];
      // @ts-expect-error TS2464 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
      return { ...accumulatedFacetValues, [facet]: currentFacetValue };
    }, {});
  }
  return {};
};

// Utility functions moved from AllSearchFiltersPanel
export const getCustomFilterGroupLabel = (): Record<string, string> => ({
  productTypeDescription: _t('Learning Product'),
  productDifficultyLevel: _t('Level'),
  topic: _t('Topic'),
});

export const getAppliedFiltersForGroup = (groupProperty: string, facetFilters: string[][] | undefined): string[] => {
  if (!facetFilters || facetFilters.length === 0) return [];

  return facetFilters
    .flat()
    .filter((filter) => filter.startsWith(`${groupProperty}:`))
    .map((filter) => filter.split(':')[1])
    .filter((value): value is string => Boolean(value));
};

export const getFilterLabelsForGroup = (
  groupProperty: string,
  facetFilters: string[][] | undefined,
  filterKeyToOptionsMap: Record<string, { value: string; label: string }[] | undefined>
): string[] => {
  const appliedValues = getAppliedFiltersForGroup(groupProperty, facetFilters);
  const groupOptions = filterKeyToOptionsMap[groupProperty] || [];

  return appliedValues
    .map((value) => {
      // Special handling for Coursera Plus filter
      if (groupProperty === IS_PART_OF_COURSERA_PLUS && value === 'true') {
        return _t('Applied');
      }

      const option = groupOptions.find((opt) => opt.value === value);
      return option ? option.label : value;
    })
    .filter((label): label is string => Boolean(label));
};
